import { useState } from 'react';
import Modal from './ui/Modal';
import BACKEND_URL from '../config';
import Cookies from 'js-cookie';
import { IconMessage2, IconStar, IconStarFilled, IconX, IconMessagePlus, IconChevronDown, IconChevronRight, IconFileText } from '@tabler/icons-react';
import { toast } from 'react-toastify';
import { Event } from '../types';
import HumanOperatorMessageModal from './HumanOperatorMessageModal';
import Button from './ui/Button';
import { FullPdfViewerLazy } from './ui/FullPDFViewerLazy';
import type { FeedbackSummary } from './Meldings';

interface MeldingProps {
  event: Event;
  showAIActions: boolean;
  setSelectedCharacteristicsEstimationEventId: (eventId: number) => void;
  setSelectedDocumentationSearchEventId: (eventId: number) => void;
  selectedCharacteristicsEstimationEventId: number | null;
  selectedDocumentationSearchEventId: number | null;
  selectedAIOperatorEventId?: number | null;
  setSelectedAIOperatorEventId?: (eventId: number | null) => void;
  selectAIOperatorEvent?: (aiOperatorEventId: number, characteristicsId?: number, documentationId?: number) => void;
  selectedAIOperatorSummaryEventId?: number | null;
  setSelectedAIOperatorSummaryEventId?: (eventId: number | null) => void;
  selectedRequestMessageEventId?: number | null;
  selectedLastSeenEventId?: number | null;
  setSelectedRequestMessageEventId?: (eventId: number | null) => void;
  setSelectedLastSeenEventId?: (eventId: number | null) => void;
  resetAllMeldingTrackingIds?: () => void;
  simulationToken?: string | null;
  onEventCreated?: (event: Event) => void;
  feedback?: FeedbackSummary;
  onFeedbackSubmitted?: (eventId: number, rating: number) => void;
}

const stars = [1, 2, 3, 4, 5] as const;

const Melding = ({ event, showAIActions, setSelectedCharacteristicsEstimationEventId, setSelectedDocumentationSearchEventId, selectedCharacteristicsEstimationEventId, selectedDocumentationSearchEventId, selectedAIOperatorEventId, setSelectedAIOperatorEventId, selectAIOperatorEvent, selectedAIOperatorSummaryEventId, setSelectedAIOperatorSummaryEventId, selectedRequestMessageEventId, selectedLastSeenEventId, setSelectedRequestMessageEventId, setSelectedLastSeenEventId, resetAllMeldingTrackingIds, simulationToken, onEventCreated, feedback, onFeedbackSubmitted }: MeldingProps) => {
  const isAIOperator = event.message_sender === 'ai_operator';
  const eventId = event.id;
  const isAISummary = event.event_type === 'ai_summary';
  const isDocSearch = event.event_type === 'documentation_search';
  const isSelectable = event.event_type === 'characteristics_estimation' || isDocSearch;
  const isSelected = (
    event.event_type === 'characteristics_estimation' && selectedCharacteristicsEstimationEventId === eventId
  ) || (
    event.event_type === 'documentation_search' && selectedDocumentationSearchEventId === eventId
  ) || (
    isAIOperator && selectedAIOperatorEventId === eventId
  ) || (
    isAISummary && selectedAIOperatorSummaryEventId === eventId
  ) || (
    ['message', 'human_characteristic_estimation', 'unit_status_update'].includes(event.event_type) &&
    (selectedRequestMessageEventId === eventId || selectedLastSeenEventId === eventId)
  );

  const [showModal, setShowModal] = useState<boolean>(false);
  const [showSuggestModal, setShowSuggestModal] = useState<boolean>(false);
  const [rating, setRating] = useState<number>(0);
  const [hoverRating, setHoverRating] = useState<number>(0);
  const [comment, setComment] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [showDocModal, setShowDocModal] = useState<boolean>(false);
  const [currentDocUrl, setCurrentDocUrl] = useState<string>('');
  const [docUrlLoading, setDocUrlLoading] = useState<boolean>(false);

  const effectiveRating = hoverRating || rating;

  const hasDocumentDetails = isAIOperator && !!event.other_arguments?.document_title;

  const open = () => setShowModal(true);
  const close = () => {
    setShowModal(false);
    setHoverRating(0);
  };

  const openDocByDocumentId = async (documentId: string) => {
    setDocUrlLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/vnog/documents/${documentId}/presigned_url/`, { credentials: 'include' });
      if (!res.ok) return;
      const data = await res.json();
      if (data.raw_document_presigned_url) {
        setCurrentDocUrl(data.raw_document_presigned_url);
        setShowDocModal(true);
      }
    } finally {
      setDocUrlLoading(false);
    }
  };

  const submit = async () => {
    if (!eventId) return;
    setSubmitting(true);
    try {
      const response = await fetch(`${BACKEND_URL}/vnog/create_ai_operator_feedback/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': Cookies.get('csrftoken') || '',
        },
        credentials: 'include',
        body: JSON.stringify({ event_id: eventId, comment, rating }),
      });
      if (!response.ok) {
        toast.error('AI Operator feedback failed');
      } else {
        onFeedbackSubmitted?.(eventId, rating);
      }
      close();
      setRating(0);
      setComment('');
    } finally {
      setSubmitting(false);
    }
  };

  const describeEventType = (eventType: string, sender: string): { label: string; pillClass: string } => {
    switch (eventType) {
      case 'human_characteristic_estimation':
        return { label: 'Human Characteristic Estimation', pillClass: 'bg-indigo-100 text-indigo-800' };
      case 'unit_status_update':
        return { label: 'Unit Status Update', pillClass: 'bg-amber-100 text-amber-800' };
      case 'ai_operator':
        return { label: 'AI Operator', pillClass: 'bg-purple-100 text-purple-800' };
      case 'ai_summary':
        return { label: 'AI Summary', pillClass: 'bg-fuchsia-100 text-fuchsia-800' };
      case 'characteristics_estimation':
        return { label: 'AI Characteristics Estimation', pillClass: 'bg-slate-100 text-slate-800' };
      case 'documentation_search':
        return { label: 'Documentation Search', pillClass: 'bg-cyan-100 text-cyan-800' };
      case 'message':
      default:
        if (sender === 'ai_operator') {
          return { label: 'AI Operator', pillClass: 'bg-purple-100 text-purple-800' };
        }
        return { label: 'Human Operator', pillClass: 'bg-emerald-100 text-emerald-800' };
    }
  };

  const prettifyMessageContent = (messageContent: string, eventType: string) => {
    if (eventType === 'characteristics_estimation') return "Characteristics estimation completed";
    if (eventType === 'documentation_search') return "Documentation search completed";
    return messageContent;
  };

  if (!showAIActions && (event.event_type === 'characteristics_estimation' || event.event_type === 'documentation_search')) return <></>;

  return (
    <div className={`p-3 rounded-lg transition-colors ${isSelected ? 'bg-blue-50 ring-1 ring-blue-300' : 'bg-gray-50 hover:bg-gray-100'}`}>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {hasDocumentDetails && (
            <button
              type="button"
              onClick={() => setExpanded(prev => !prev)}
              className="text-gray-500 hover:text-gray-700"
            >
              {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
            </button>
          )}
          <span className="text-gray-700 flex items-center gap-2 flex-wrap">
            <span className="font-medium text-gray-900">{event.adjusted_start_time}:</span>
            {(() => {
              const { label, pillClass } = describeEventType(event.event_type, event.message_sender);
              return (
                <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${pillClass}`}>
                  {label}
                </span>
              );
            })()}
            <span>{prettifyMessageContent(event.message_content, event.event_type)}</span>
          </span>
        </div>
        <div className="flex items-center gap-2">
          {(isAIOperator || isAISummary) && (
            <button
              type="button"
              onClick={() => {
                if (!eventId) return;
                const characteristicsId = (event as any)?.other_arguments?.characteristics_estimation_event_id as number | undefined;
                const documentationId = (event as any)?.other_arguments?.documentation_search_event_id as number | undefined;
                const requestMessageId = (event as any)?.other_arguments?.request_message_event_id as number | undefined;
                const lastSeenId = (event as any)?.other_arguments?.last_seen_event_id as number | undefined;
                if (resetAllMeldingTrackingIds) resetAllMeldingTrackingIds();
                if (selectAIOperatorEvent) {
                  selectAIOperatorEvent(eventId, characteristicsId, documentationId);
                } else {
                  if (setSelectedAIOperatorEventId) setSelectedAIOperatorEventId(eventId);
                  if (isAISummary && setSelectedAIOperatorSummaryEventId) setSelectedAIOperatorSummaryEventId(eventId);
                  if (typeof characteristicsId === 'number') setSelectedCharacteristicsEstimationEventId(characteristicsId);
                  if (typeof documentationId === 'number') setSelectedDocumentationSearchEventId(documentationId);
                  if (typeof requestMessageId === 'number' && setSelectedRequestMessageEventId) setSelectedRequestMessageEventId(requestMessageId);
                  if (typeof lastSeenId === 'number' && setSelectedLastSeenEventId) setSelectedLastSeenEventId(lastSeenId);
                }
              }}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-900 text-white hover:bg-black'
              }`}
              aria-label="Select AI Operator event"
            >
              Select
            </button>
          )}
          {isSelectable && (
            <button
              type="button"
              onClick={() => {
                if (!eventId) return;
                if (event.event_type === 'characteristics_estimation') {
                  setSelectedCharacteristicsEstimationEventId(eventId);
                } else if (event.event_type === 'documentation_search') {
                  setSelectedDocumentationSearchEventId(eventId);
                }
              }}
              disabled={!eventId}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                !eventId ? 'bg-gray-200 text-gray-500 cursor-not-allowed' : isSelected ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-900 text-white hover:bg-black'
              }`}
              aria-label="Select event"
            >
              Select
            </button>
          )}
          {isDocSearch && simulationToken && (
            <button
              type="button"
              onClick={() => setShowSuggestModal(true)}
              disabled={!eventId}
              className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                eventId ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
              }`}
              aria-label="Suggest operator message"
            >
              <IconMessagePlus size={14} />
              Suggest Message
            </button>
          )}
          {isAIOperator && (
            <>
              {(feedback?.count ?? 0) > 0 && (
                <div className="inline-flex items-center gap-1" aria-label={`Feedback: ${feedback!.latestRating}/5`}>
                  {stars.map((star) => (
                    star <= feedback!.latestRating ? (
                      <IconStarFilled key={star} size={16} className="text-amber-400" />
                    ) : (
                      <IconStar key={star} size={16} className="text-amber-300" />
                    )
                  ))}
                  <span className="ml-1 text-xs text-gray-600">({feedback!.count})</span>
                </div>
              )}
              <button
                type="button"
                onClick={open}
                disabled={!eventId}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-xs transition-colors ${
                  eventId ? 'bg-gray-900 text-white hover:bg-black' : 'bg-gray-200 text-gray-500 cursor-not-allowed'
                }`}
              >
                <IconMessage2 size={14} />
                Feedback
              </button>
            </>
          )}
        </div>
      </div>

      {hasDocumentDetails && expanded && (
        <div className="mt-3 ml-6 pl-4 border-l-2 border-gray-300">
          <h4 className="font-medium text-gray-900 break-words whitespace-normal truncate max-w-[400px]">
            {event.other_arguments.document_title}
          </h4>
          {event.other_arguments.supporting_quotes && (
            <div className="mt-2 text-gray-700 text-sm">
              <p className="font-medium mb-1">Supporting Quotes:</p>
              <p className="italic">{event.other_arguments.supporting_quotes}</p>
            </div>
          )}
          {event.other_arguments.document_id && (
            <Button
              onClick={() => openDocByDocumentId(event.other_arguments.document_id)}
              disabled={docUrlLoading}
              className="mt-2 flex items-center gap-1 text-sm"
            >
              <IconFileText size={16} />
              {docUrlLoading ? 'Loading...' : 'View Document'}
            </Button>
          )}
        </div>
      )}

      <Modal isOpen={showModal} onRequestClose={close}>
        <div className="w-full max-w-lg mx-auto">
          <div className="relative rounded-xl bg-white shadow-lg border border-gray-100 p-5">
            <button
              type="button"
              onClick={close}
              className="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 text-gray-500"
              aria-label="Close"
            >
              <IconX size={18} />
            </button>

            <div className="mb-3">
              <h3 className="text-base font-semibold text-gray-900">Rate this AI suggestion</h3>
              <p className="text-sm text-gray-500 mt-1">How helpful was this AI Operator message? Select a rating and add an optional comment.</p>
            </div>

            <div className="flex items-center gap-1 mb-3" aria-label="Star rating selector">
              {stars.map((star) => {
                const active = star <= effectiveRating;
                return (
                  <button
                    key={star}
                    type="button"
                    className="p-1 transition-transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-300 rounded"
                    onMouseEnter={() => setHoverRating(star)}
                    onMouseLeave={() => setHoverRating(0)}
                    onClick={() => setRating(star)}
                    aria-label={`${star} star${star > 1 ? 's' : ''}`}
                  >
                    {active ? (
                      <IconStarFilled size={28} className="text-amber-400" />
                    ) : (
                      <IconStar size={28} className="text-amber-300" />
                    )}
                  </button>
                );
              })}
              <span className="ml-2 text-sm text-gray-600">{rating > 0 ? `${rating}/5` : 'Select a rating'}</span>
            </div>

            <div className="mb-3">
              <label htmlFor="ai-feedback-comment" className="block text-sm font-medium text-gray-700 mb-1">Add a brief comment (optional)</label>
              <textarea
                id="ai-feedback-comment"
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                rows={3}
                placeholder="What worked well? What could be improved?"
                className="w-full resize-y rounded-lg border border-gray-200 focus:border-blue-400 focus:ring-2 focus:ring-blue-100 outline-none p-2 text-sm text-gray-800 placeholder-gray-400"
              />
              <p className="text-xs text-gray-400 mt-1">Your feedback helps improve AI guidance.</p>
            </div>

            <div className="flex items-center justify-end gap-2">
              <button type="button" onClick={close} className="px-3 py-2 rounded-md text-sm font-medium border border-gray-200 text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                type="button"
                disabled={submitting || rating === 0 || !eventId}
                onClick={submit}
                className={`px-4 py-2 rounded-md text-white text-sm font-medium transition-colors ${submitting || rating === 0 || !eventId ? 'bg-gray-300 cursor-not-allowed' : 'bg-blue-600 hover:bg-blue-700'}`}
              >
                {submitting ? 'Submitting…' : 'Submit feedback'}
              </button>
            </div>
          </div>
        </div>
      </Modal>

      {isDocSearch && simulationToken && eventId && (
        <HumanOperatorMessageModal
          isOpen={showSuggestModal}
          onClose={() => setShowSuggestModal(false)}
          documentationSearchEventId={eventId}
          simulationToken={simulationToken}
          onEventCreated={(newEvent) => {
            if (onEventCreated) onEventCreated(newEvent);
          }}
        />
      )}

      <Modal isOpen={showDocModal} onRequestClose={() => setShowDocModal(false)}>
        <div className="w-[90vw] h-[90vh] bg-white rounded-lg flex flex-col">
          <div className="flex justify-between items-center p-4 border-b">
            <h2 className="text-xl font-semibold">Document Viewer</h2>
            <button
              onClick={() => setShowDocModal(false)}
              className="p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <IconX size={24} stroke={1.5} />
            </button>
          </div>
          <div className="flex-1 p-4 overflow-y-auto">
            <FullPdfViewerLazy pdfUrl={currentDocUrl} />
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Melding;


